import { createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";

const compareAlerts = (a1, a2) => a1.alertid === a2.alertid;

const alertSlice = createSlice({
    name: 'alerts',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const alert of ensureArray(action.payload)) {
                const idx = state.data.findIndex(a => compareAlerts(a, alert));
                if (idx > -1) {
                    state.data.splice(idx, 1, alert);
                } else {
                    state.data.push(alert);
                }
            }
        },
        remove: (state, action) => {
            for (const alert of ensureArray(action.payload)) {
                const idx = state.data.findIndex(a => compareAlerts(a, alert));
                if (idx > -1) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => alertSlice.getInitialState());
    }
});

// #region network operations
const fetchAlerts = network => {
    return network.get('/api/v1/alerts', alertSlice.actions.set);
}
export const addAlert = (network, alert) => network.post('/api/v1/alert', alert, alertSlice.actions.addOrUpdate);
export const updateAlert = (network, alert) => network.put('/api/v1/alert', alert, alertSlice.actions.addOrUpdate);
export const removeAlert = (network, alert) => network.delete('/api/v1/alert/' + encodeURIComponent(alert.alertid), alertSlice.actions.remove);
export const markAlertSeen = (network, alert) => network.put('/api/v1/alertSeen', alert, alertSlice.actions.addOrUpdate);
export const flagAlert = (network, alert) => network.put('/api/v1/alertFlagged', { alertid: alert.alertid }, alertSlice.actions.addOrUpdate);
export const unflagAlert = (network, alert) => network.put('/api/v1/alertUnflagged', {alertid: alert.alertid }, alertSlice.actions.addOrUpdate);
// #endregion

// #region selectors
const selectAlerts = state => state.alerts.data;
// #endregion

// #region permissions
export const canCreateAlert = (credentials, alert) => credentials.access.hasFull(['administrator', 'dispatcher']);
export const canUpdateAlert = canCreateAlert;
export const canRemoveAlert = (credentials, alert) => canCreateAlert(credentials, alert) && !alert.system;
// #endregion

// #region listeners
const listen = {
    alertCreated: (alert, { dispatch }) => dispatch(alertSlice.actions.addOrUpdate(alert)),
    alertUpdated: (alert, { dispatch }) => dispatch(alertSlice.actions.addOrUpdate(alert)),
    alertDeleted: (alert, { dispatch }) => dispatch(alertSlice.actions.remove(alert))
}
// #endregion

export const [useAlerts] = dataHookFactory(selectAlerts, { run: fetchAlerts, listen });

const reducerDetails = { name: alertSlice.name, reducer: alertSlice.reducer };
export default reducerDetails;